/* @flow */
import * as React from 'react';
import { capitalize } from 'lodash';
import Modal from 'react-modal';

import EventTracking from 'lib/EventTracking';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Text from 'components/Text';
import MediaObject from 'components/MediaObject';
import List from 'components/List';
import ListItem from 'components/ListItem';
import Link from 'components/Link';
import Heading from 'components/Heading';
import Button from 'components/ButtonDeprecated';
import Tooltip from 'components/Tooltip';
import TooltipTrigger from 'components/TooltipTrigger';
import Dialog from 'components/Dialog';
import Divider from 'components/Divider';
import Row from 'components/Row';

import IndividualPlanLink from './IndividualPlanLink';

import type { IndividualPlanView as IndividualPlanViewQuery } from 'settings/queries/__generated__/IndividualPlanView';

type NoMaybeMeType = $NonMaybeType<$ElementType<IndividualPlanViewQuery, 'me'>>;
type IndividualPlansType = $ElementType<$ElementType<NoMaybeMeType, 'individualPlans'>, number>;
type PlanUnionType = $ElementType<IndividualPlansType, 0>;
type PurchasableIndividualPlanType = $ElementType<PlanUnionType, 1>;
type PurchasablePlanProductDetailsType = $ElementType<
  $ElementType<PurchasableIndividualPlanType, 'productDetails'>,
  number
>;
type PurchaseOptionsType = $ElementType<
  $ElementType<PurchasableIndividualPlanType, 'purchaseOptions'>,
  number
>;

type Props = {
  inPaidOrg: boolean,
  plan: PurchasableIndividualPlanType,
};

type State = {
  showMobilePlanModal: boolean,
};

class PurchasablePlanView extends React.Component<Props, State> {
  state: State = {
    showMobilePlanModal: false,
  };

  renderPlan = (inPaidOrg: boolean, plan: PurchasableIndividualPlanType) => {
    const IndividualPlanSection = (
      <Box
        title={
          <MediaObject center figure="right">
            <MediaObject center figure="left">
              <Text align="left" size="medium" weight="semibold">
                Individual Plan
              </Text>
              <React.Fragment />
            </MediaObject>
            <React.Fragment />
          </MediaObject>
        }
        dark
        headerSize="xsmall">
        <BoxBody size="small">
          {this.renderPlanFeatures(plan.productDetails)}
          {this.renderBilling(inPaidOrg, plan.purchaseOptions)}
        </BoxBody>
      </Box>
    );

    return IndividualPlanSection;
  };

  renderBilling = (inPaidOrg: boolean, purchaseOptions: PurchaseOptionsType) => {
    const tooltip = (
      <Tooltip>
        You cannot purchase an Individual plan while you are in a School &amp; District plan
      </Tooltip>
    );

    const buttons = inPaidOrg ? (
      <MediaObject figure="right">
        <React.Fragment />
        <TooltipTrigger tooltip={tooltip}>
          <Button
            onClick={() => {
              EventTracking.trackKey('individual_plans.plan_view.paid_org_purchase.click');
            }}
            data-test="purchase-in-paid-org"
            level="primary"
            color="graphite"
            full>
            Purchase
          </Button>
        </TooltipTrigger>
      </MediaObject>
    ) : (
      <MediaObject figure="right">
        <React.Fragment />
        <Button
          onClick={() => {
            this.setState({ showMobilePlanModal: true });
            EventTracking.trackKey('individual_plans.plan_view.purchase.click');
            EventTracking.trackKey('individual_plans.sms_modal_view.view');
          }}
          data-test="purchase-not-in-paid-org"
          level="primary"
          full>
          Purchase
        </Button>
      </MediaObject>
    );

    const priceOptionItems = Object.keys(purchaseOptions).map(option =>
      option.includes('_') ? null : (
        <ListItem>
          <Text size="small" weight="semibold">
            {capitalize(option) + ' - '}
          </Text>
          <Text size="small" weight="normal">
            {' ' + purchaseOptions[option].priceText}
          </Text>
        </ListItem>
      )
    );

    const billingSection = (
      <React.Fragment>
        <Heading align="left" level="5">
          Choose a payment method that works for you. Cancel anytime
        </Heading>
        <List marker="bullet" markerPosition="inside" size="medium">
          {priceOptionItems}
        </List>
      </React.Fragment>
    );

    return (
      <React.Fragment>
        <Modal isOpen={this.state.showMobilePlanModal}>
          <Dialog
            onRequestHide={() => this.setState({ showMobilePlanModal: false })}
            eventScreenName="TextAppLinkDialog"
            className="TextAppLinkDialog"
            size="small">
            <BoxBody>
              <IndividualPlanLink />
            </BoxBody>
          </Dialog>
        </Modal>
        {billingSection}
        <Divider size="xsmall" />
        {buttons}
      </React.Fragment>
    );
  };

  renderPlanFeatures = (purchasablePlanFeatures: Array<PurchasablePlanProductDetailsType>) => {
    const listItems = purchasablePlanFeatures.map(item => (
      <ListItem>
        <Text size="small">{item.text}</Text>
        {item.learnMoreLink ? (
          <Link href={item.learnMoreLink} weight="semibold">
            {' Learn More '}
          </Link>
        ) : null}
      </ListItem>
    ));

    return (
      <Row size="small">
        <Heading align="left" level="3">
          Features
        </Heading>
        <List marker="bullet" markerPosition="inside" size="medium">
          {listItems}
        </List>
      </Row>
    );
  };

  render() {
    const { inPaidOrg, plan } = this.props;
    return <React.Fragment>{this.renderPlan(inPaidOrg, plan)}</React.Fragment>;
  }
}

export default PurchasablePlanView;
